import { useRef, useState } from 'react';
import { Alert, Button, Group, Image, SimpleGrid, Text } from '@mantine/core';
import { api, preparePhoto, uploadToStorage } from '@prooffleet/client';

export interface UploadedPhoto {
  objectKey: string;
  versionId: string | null;
  sha256: string;
  bytes: number;
  mime: string;
}

interface PresignResponse {
  url: string;
  objectKey: string;
}

/**
 * Загрузка фотографий выполненного ремонта.
 *
 * Тот же путь, что у водителя, и по тем же причинам: ресайз строго ДО
 * хеширования (правило 3), загрузка напрямую в хранилище по подписанной
 * ссылке — Node не касается байтов, — и закрепление версии объекта из
 * заголовка ответа, потому что Object Lock защищает версии, а не имена.
 */
export function RepairPhotos({
  unitId,
  photos,
  onChange,
}: {
  unitId: string;
  photos: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
}): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const prepared = await preparePhoto(file);

      const presign = await api<PresignResponse>('/uploads/presign', {
        method: 'POST',
        body: {
          unitId,
          sha256: prepared.sha256,
          kind: 'photo',
          extension: 'jpg',
          bytes: prepared.bytes.byteLength,
        },
      });

      const { versionId } = await uploadToStorage(presign.url, prepared.bytes, 'image/jpeg');

      const uploaded: UploadedPhoto = {
        objectKey: presign.objectKey,
        versionId,
        sha256: prepared.sha256,
        bytes: prepared.bytes.byteLength,
        mime: 'image/jpeg',
      };

      setPreviews((prev) => [
        ...prev,
        URL.createObjectURL(new Blob([prepared.bytes.slice()], { type: 'image/jpeg' })),
      ]);
      onChange([...photos, uploaded]);
    } catch {
      setError('Could not upload the photo');
    } finally {
      setBusy(false);
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file !== undefined) void handleFile(file);
        }}
      />

      {previews.length > 0 && (
        <SimpleGrid cols={previews.length === 1 ? 1 : 3} mb="sm">
          {previews.map((url) => (
            <Image key={url} src={url} radius="sm" alt="" h={90} fit="cover" />
          ))}
        </SimpleGrid>
      )}

      {error !== null && (
        <Alert color="red" mb="sm">
          {error}
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Button
          variant="default"
          loading={busy}
          onClick={() => inputRef.current?.click()}
        >
          {photos.length === 0 ? 'Add photo' : 'Add another'}
        </Button>
        <Text fz="xs" c="dimmed" className="pf-num">
          {photos.length} attached
        </Text>
      </Group>
    </div>
  );
}
